import React, { useState, useEffect } from 'react';
import { adminGetStats, adminGetHeroStats } from '../services/adminApi';
import { Eye, Image as ImageIcon, MessageSquare, TrendingUp, MousePointerClick } from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div
    className="admin-stat-card"
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="admin-stat-icon" style={{ background: accent }}>
      <Icon size={22} color="#fff" />
    </div>
    <div className="admin-stat-text">
      <div className="admin-stat-value">{value}</div>
      <div className="admin-stat-label">{label}</div>
    </div>
  </div>
);

const AdminAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [heroStats, setHeroStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const data = await adminGetStats();
        setStats(data);
        const hero = await adminGetHeroStats();
        setHeroStats(hero.items || []);
      } catch (err) {
        setError('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div data-testid="analytics-loading">Loading analytics...</div>;
  if (error) return <div className="login-error">{error}</div>;
  if (!stats) return null;

  const maxCategoryViews = Math.max(1, ...stats.category_breakdown.map(c => c.views));
  const dailyViews = stats.daily_views || [];
  const maxDailyViews = Math.max(1, ...dailyViews.map(d => d.count));
  const dailyTotal = dailyViews.reduce((acc, d) => acc + d.count, 0);

  return (
    <div data-testid="admin-analytics">
      <h2 className="admin-section-title">Analytics Overview</h2>

      <div className="admin-stats-grid">
        <StatCard icon={ImageIcon} label="Total Images" value={stats.total_images} accent="#d4734b" />
        <StatCard icon={Eye} label="Total Views" value={stats.total_views} accent="#5b8def" />
        <StatCard icon={MessageSquare} label="New Messages" value={stats.new_messages} accent="#3bb371" />
        <StatCard icon={TrendingUp} label="Messages (30d)" value={stats.messages_last_30_days} accent="#a061d1" />
      </div>

      <div className="admin-card admin-analytics-card" style={{ marginBottom: '1.5rem' }} data-testid="daily-views-chart">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' }}>
          <h3 className="admin-analytics-title" style={{ margin: 0 }}>Daily Views (last {dailyViews.length} days)</h3>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>
            {dailyTotal} view{dailyTotal === 1 ? '' : 's'} in window
          </span>
        </div>
        {dailyViews.length === 0 ? (
          <p style={{ color: '#888' }}>No data yet.</p>
        ) : (
          <div className="admin-daily-chart" data-testid="daily-views-bars">
            {dailyViews.map((d) => {
              const heightPct = (d.count / maxDailyViews) * 100;
              return (
                <div
                  key={d.date}
                  className="admin-daily-bar-col"
                  title={`${d.date}: ${d.count}`}
                  data-testid={`daily-bar-${d.date}`}
                >
                  <div
                    className="admin-daily-bar"
                    style={{ height: d.count === 0 ? '2px' : `${Math.max(heightPct, 3)}%` }}
                    data-count={d.count}
                  />
                  <span className="admin-daily-bar-label">{d.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {heroStats && (
        <div className="admin-card admin-analytics-card" style={{ marginBottom: '1.5rem' }} data-testid="hero-engagement-card">
          <h3 className="admin-analytics-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <MousePointerClick size={18} /> Hero Menu Engagement
          </h3>
          {heroStats.every((h) => h.hovers === 0 && h.clicks === 0) ? (
            <p style={{ color: '#888' }}>No interactions recorded yet. Visit the homepage and hover/click the orbit menu.</p>
          ) : (
            <ul className="admin-category-bars">
              {heroStats.map((h) => {
                const maxVal = Math.max(1, ...heroStats.map((x) => Math.max(x.hovers, x.clicks)));
                return (
                  <li key={h.item} className="admin-category-bar-row" data-testid={`hero-bar-${h.item}`}>
                    <div className="admin-category-bar-label">
                      <span style={{ textTransform: 'capitalize' }}>{h.item}</span>
                      <span style={{ color: '#888' }}>{h.hovers} hovers · {h.clicks} clicks</span>
                    </div>
                    <div className="admin-category-bar-track" style={{ marginBottom: '4px' }}>
                      <div
                        className="admin-category-bar-fill"
                        style={{ width: `${(h.hovers / maxVal) * 100}%`, background: '#c9975b' }}
                        title={`${h.hovers} hovers`}
                      />
                    </div>
                    <div className="admin-category-bar-track">
                      <div
                        className="admin-category-bar-fill"
                        style={{ width: `${(h.clicks / maxVal) * 100}%` }}
                        title={`${h.clicks} clicks`}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <div className="admin-analytics-row">
        <div className="admin-card admin-analytics-card">
          <h3 className="admin-analytics-title">Top Viewed Images</h3>
          {stats.top_images.length === 0 || stats.total_views === 0 ? (
            <p style={{ color: '#888' }}>No views recorded yet. Open the public gallery to start tracking.</p>
          ) : (
            <ul className="admin-top-images-list" data-testid="top-images-list">
              {stats.top_images.map((img) => (
                <li key={img.id} className="admin-top-image-row">
                  <img src={img.thumbnail_url} alt={img.title || 'image'} />
                  <div className="admin-top-image-meta">
                    <div className="admin-top-image-title">{img.title || '(Untitled)'}</div>
                    <div className="admin-top-image-cats">
                      {(img.category || []).map((c) => (
                        <span key={c} className="admin-gallery-category">{c}</span>
                      ))}
                    </div>
                  </div>
                  <div className="admin-top-image-views">
                    <Eye size={14} /> {img.views}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="admin-card admin-analytics-card">
          <h3 className="admin-analytics-title">Views by Category</h3>
          {stats.category_breakdown.length === 0 ? (
            <p style={{ color: '#888' }}>No categories yet.</p>
          ) : (
            <ul className="admin-category-bars">
              {stats.category_breakdown.map((c) => (
                <li key={c.category} className="admin-category-bar-row" data-testid={`cat-bar-${c.category}`}>
                  <div className="admin-category-bar-label">
                    <span>{c.category}</span>
                    <span style={{ color: '#888' }}>{c.views} views · {c.count} img</span>
                  </div>
                  <div className="admin-category-bar-track">
                    <div
                      className="admin-category-bar-fill"
                      style={{ width: `${(c.views / maxCategoryViews) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
